// Per-form pharmacokinetic constants for 7-OH and kratom-leaf doses.
// Closes Gap 4 of the powder-tracking sprint.
//
// Used by:
//   - shared/computeDoseTimeline (new in this commit) to derive
//     effective-onset, peak, clearance, and earliest withdrawal-onset
//     timestamps for a given dose.
//   - The DoseTimingCard surface on TodayScreen, which translates the
//     above into glanceable copy for users transitioning between forms
//     (especially tablet → powder, where the curve is dramatically
//     different).
//
// These are NOT clinical claims. They're operational estimates the app
// uses to give users a felt-time-frame for their dose, calibrated from
// published kratom and synthetic-opioid PK data. Each user's actual
// curve varies with CYP3A4 expression, gut emptying, fed/fasted state,
// concurrent meds, and prior tolerance. The card framing is always
// "approximate based on typical PK"; no medical decisions hang on it.
//
// Citations:
//   - Hanapi et al., 2024 — PMC10934259 (kratom leaf PK in humans).
//     Mitragynine T_max ≈ 1-2 h, T_1/2 ≈ 43 h single dose, 67 h multi.
//   - FDA 7-OH scientific assessment, 2025 (media/187899/download).
//     7-OH oral T_max ≈ 0.5-1 h, plasma T_1/2 ≈ 3 h.
//   - Avery et al., 2019 — receptor pharmacology comparison.

export interface FormPk {
  /** Minutes from ingestion to noticeable subjective onset. */
  onsetMinutes: number;
  /** Minutes from ingestion to subjective peak. */
  peakMinutes: number;
  /**
   * Hours after which the dose is "mostly cleared" for the purposes of
   * felt effect. Roughly 4× the half-life of the dominant active
   * alkaloid. For powder this is much longer than a tablet because
   * mitragynine's half-life is dramatically longer than 7-OH's.
   */
  clearanceHours: number;
  /**
   * Hours after the last dose at which withdrawal-onset becomes
   * plausible for a dependent user. NOT a precise prediction — it's the
   * "you might start feeling something soon" floor. For tablet 7-OH
   * this is short; for powder it's much longer because the residual
   * mitragynine maintains opioid agonism past the felt-peak.
   */
  earliestWithdrawalOnsetHours: number;
  /** Display string for "dominant active alkaloid in this form." */
  dominantAlkaloid: string;
}

export const PK_BY_FORM: Record<'tablet_7oh' | 'leaf' | 'extract', FormPk> = {
  // Pure 7-OH tablets: short, sharp curve. Peak within an hour;
  // mostly cleared within 12 hours. Withdrawal can hit within 4-8h
  // for a dependent user.
  tablet_7oh: {
    onsetMinutes: 15,
    peakMinutes: 30,
    clearanceHours: 12,
    earliestWithdrawalOnsetHours: 4,
    dominantAlkaloid: '7-hydroxymitragynine',
  },
  // Plain leaf / powder: mitragynine-dominant. Slower onset, longer
  // peak window, very long clearance (mitragynine T_1/2 of 43-68h
  // means residual alkaloid persists for days). Withdrawal-onset
  // floor is meaningfully higher than tablets.
  leaf: {
    onsetMinutes: 30,
    peakMinutes: 90,
    clearanceHours: 72,
    earliestWithdrawalOnsetHours: 12,
    dominantAlkaloid: 'mitragynine (with 7-OH metabolite)',
  },
  // Extracts vary wildly. Full-spectrum extracts behave like leaf
  // (mitragynine-dominant); isolated 7-OH extracts behave like
  // tablets. We pick a middle ground that errs toward leaf — most
  // extracts in the wild are full-spectrum.
  extract: {
    onsetMinutes: 20,
    peakMinutes: 60,
    clearanceHours: 48,
    earliestWithdrawalOnsetHours: 8,
    dominantAlkaloid: 'mixed (mitragynine + 7-OH)',
  },
} as const;

export interface DoseTimeline {
  /** ISO timestamp the dose was taken. */
  ts: string;
  /** Form-specific PK reference. */
  pk: FormPk;
  /** ISO timestamp of expected onset (ts + onsetMinutes). */
  onsetAt: string;
  /** ISO timestamp of expected peak (ts + peakMinutes). */
  peakAt: string;
  /** ISO timestamp of "mostly cleared" (ts + clearanceHours). */
  clearedAt: string;
  /** ISO timestamp earliest withdrawal-onset becomes plausible. */
  withdrawalOnsetAt: string;
  /**
   * Hours from "now" until earliest withdrawal-onset. Negative means
   * onset window has already passed (i.e. the user is now in the
   * "withdrawal possible at any time" zone if they haven't redosed).
   */
  hoursUntilWithdrawalOnset: number;
}

/**
 * Compute the form-specific PK timeline for a dose. Pure function;
 * deterministic given ts + form + a reference "now" for relative
 * calculations.
 */
export function computeDoseTimeline(
  ts: string,
  form: 'tablet_7oh' | 'leaf' | 'extract',
  now: Date = new Date(),
): DoseTimeline {
  const pk = PK_BY_FORM[form];
  const start = new Date(ts);
  const onsetAt = new Date(start.getTime() + pk.onsetMinutes * 60_000);
  const peakAt = new Date(start.getTime() + pk.peakMinutes * 60_000);
  const clearedAt = new Date(start.getTime() + pk.clearanceHours * 3_600_000);
  const withdrawalOnsetAt = new Date(
    start.getTime() + pk.earliestWithdrawalOnsetHours * 3_600_000,
  );
  const hoursUntilWithdrawalOnset =
    (withdrawalOnsetAt.getTime() - now.getTime()) / 3_600_000;
  return {
    ts,
    pk,
    onsetAt: onsetAt.toISOString(),
    peakAt: peakAt.toISOString(),
    clearedAt: clearedAt.toISOString(),
    withdrawalOnsetAt: withdrawalOnsetAt.toISOString(),
    hoursUntilWithdrawalOnset,
  };
}
